import {
  useEffect,
  useRef,
  useState,
  type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../lib/routes";
import { useUiFeatures, useInClass, useKids } from "../lib/uiFeaturesApi";
import { track } from "../lib/ga4";
import { useHeroStore } from "../store/heroStore";
import { HeroCustomizer } from "../ui/HeroCustomizer";
import { HeroAvatar } from "../ui/HeroAvatar";
import { useUiArt } from "../lib/uiArt";
import { uiArtSlot,
  uiVisible,
  landingCharHome,
  LANDING_HERO_HOME,
} from "@greg-chick/shared";
import { EventBanner } from "../ui/EventBanner";
import { loadMark, worthResuming } from "../book/readingProgress";
import { loadBooksList } from "../book/bookData";
import { isRallyCompleted } from "../lib/rallyState";
import { loadLandingAssets, landingAssetsNow, type LandingAssets } from "../lib/landingAssets";
import { heroDisplayName } from "../lib/heroName";
import { SpriteStrip } from "../ui/SpriteStrip";
import { SettingsSheet } from "../ui/SettingsSheet";
import { getSoundPref, setSoundPref } from "../lib/soundPref";
import { loadSiteText, resetSiteText } from "../lib/siteTextApi";
import { DEFAULT_SITE_TEXT, withoutEdgeEmoji, type SiteText } from "@greg-chick/shared";
import { UiIcon } from "../ui/UiIcon";
import { AgeBands } from "../ui/AgeBands";
import { stagePosForWidth, LANDING_NARROW_MAX } from "@greg-chick/shared";
import { subscribeContentUpdates } from "../lib/liveReload";

// 메인 화면(UI/UX 설계 4-①): 상단바 + 히어로 무대(배경·캐릭터·이펙트) + 우측 메뉴 + CTA + 하단 고정 7메뉴.
// 배경/캐릭터는 매니페스트로 교체 가능, 이펙트·애니메이션은 data 속성으로 교체 가능.

// 교체 가능한 연출 설정(추후 매니페스트/관리자 연동 여지)
const EFFECT: "fireflies" | "petals" | "sparkles" = "fireflies";
const CHAR_ANIM = "bob"; // 캐릭터 idle 애니(bob/float/sway)

// 무대 내 기본 자리는 **shared 한 곳**(landingCharHome·LANDING_HERO_HOME) —
// 관리자 미리보기와 같은 답을 봐야 한다(안 그러면 새로 올린 그림이 서로 다른 자리에 선다).
const HERO_DEF = LANDING_HERO_HOME;
// 캐릭터 이름표.
const CHAR_ALT: Record<string, string> = { greg: "메인 왼쪽 캐릭터", chick: "병아리" };
function pieceStyle(
  p: { x: number; y: number } | undefined,
  def: { x: number; y: number },
  scale?: number,
  z?: number,
): CSSProperties {
  return {
    left: `${(p?.x ?? def.x) * 100}%`,
    top: `${(p?.y ?? def.y) * 100}%`,
    transform: `translate(-50%, -50%) scale(${scale ?? 1})`,
    ...(typeof z === "number" ? { zIndex: z } : null), // 앞뒤 순서(미설정=기본 배치순서)
  };
}

export function Landing() {
  const navigate = useNavigate();
  const hydrate = useHeroStore((s) => s.hydrate);
  const saved = useHeroStore((s) => s.saved);
  const draft = useHeroStore((s) => s.draft);
  const [customizing, setCustomizing] = useState(false);
  /**
   * 좁은 화면인가 — 폰에서 캐릭터가 왼쪽에 몰리지 않게 병아리를 히어로 오른쪽으로 넘긴다.
   * 폭이 바뀌면 바로 따라간다(폰을 가로로 돌리는 경우도 포함). 판정은 shared 한 곳.
   */
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= LANDING_NARROW_MAX,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${LANDING_NARROW_MAX}px)`);
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // 🔴 **빈 무대로 시작하지 않는다.** 예전엔 게시본을 기다리는 동안(실측 0.7~2.5초)
  //    파란 그라데이션에 절차적 아바타만 서 있었다 — 첫인상이 그것이었다.
  const [assets, setAssets] = useState<LandingAssets>(landingAssetsNow);
  /**
   * 🖼️ **큰 그림이 먼저, 꾸밈은 그다음** (2026-09-25 · 조치계획서 ■2 · FR-LAND-01).
   *
   * 느린 회선(1.6Mbps)에서 두 약속이 부딪혔다 — ■2 의 「화면 뜸 1.3초」와 FR-LAND-01 의
   * 「4G LCP 2.5초」. `index.html` 에서 큰 그림을 미리 받으면 코드와 회선을 나눠 써서 화면이
   * 늦게 뜨고(1.57초), 미리 받기를 빼면 큰 그림이 아이콘·캐릭터 350KB 와 회선을 나눠 써서
   * 늦게 온다(LCP 3.34초). 순서를 **코드 → 큰 그림 → 나머지**로 세우면 둘 다 된다.
   *
   * 그래서 큰 그림이 도착할 때까지 아이콘·캐릭터·히어로·바 그림을 **안 부른다.**
   * 대가: 처음 잠깐 메뉴 아이콘 자리가 비어 있다(글자는 있다 — 누를 수 있다).
   *
   * 🔴 **영영 기다리지 않는다.** 큰 그림이 실패하거나 너무 늦으면 `DECOR_WAIT_MS` 뒤에 그냥 부른다
   * — 한 장 때문에 메뉴 그림이 통째로 안 나오면 안 된다(불변식 3).
   */
  const [decor, setDecor] = useState(() => !landingAssetsNow().bg);
  const bgRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    if (decor) return;
    // 캐시에서 온 그림은 onLoad 가 붙기 전에 이미 끝나 있을 수 있다
    if (bgRef.current?.complete) {
      setDecor(true);
      return;
    }
    const t = window.setTimeout(() => setDecor(true), DECOR_WAIT_MS);
    return () => window.clearTimeout(t);
  }, [decor]);
  const [toast, setToast] = useState<string | null>(null);
  // 홈 문구는 운영자가 관리자에서 바꾼다("체험판 공개 → 정식 오픈" 등). 못 받으면 기본값.
  const [text, setText] = useState<SiteText>(DEFAULT_SITE_TEXT);
  const rallyDone = isRallyCompleted();
  const gregChar = assets.chars.find((c) => c.id === "greg"); // 말풍선 앵커(왼쪽 캐릭터 머리 위)

  // 네이티브 alert 대신 테마 토스트(영유아 앱 — 차분·일관)
  const flash = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2600);
  };

  useEffect(() => {
    hydrate();
  }, [hydrate]);
  useEffect(() => {
    let alive = true;
    void loadLandingAssets().then((a) => alive && setAssets(a));
    return () => {
      alive = false;
    };
  }, []);
  /**
   * 읽다 만 책 — **이 기기 안에만** 있는 표시(무수집). 있으면 첫 화면에서 바로 이어 간다.
   * 아이는 한 번에 다 안 읽는데, 다시 들어오면 책 고르기부터 해야 했다.
   * 책 이름은 목록에서 찾아 붙인다 — 이름을 못 찾으면(지워진 책) 버튼을 아예 안 그린다.
   */
  const [resume, setResume] = useState<{ id: string; page: number; title: string } | null>(null);
  useEffect(() => {
    let alive = true;
    const mark = loadMark();
    if (!worthResuming(mark)) return;
    void loadBooksList()
      .then((list) => {
        if (!alive) return;
        const hit = list.find((b) => b.id === mark.bookId);
        // 쪽수를 알고 나서 한 번 더 본다 — **다 읽은 책은 이어서 읽기로 권하지 않는다.**
        // 누르면 뒤표지에 떨어지고, 그 쪽에 머무는 동안 표시가 계속 갱신돼 영영 그대로 남는다.
        if (hit && worthResuming(mark, hit.pages)) {
          setResume({ id: mark.bookId, page: mark.page, title: hit.title });
        }
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  // 문구 로드 + 게시 즉시 반영(운영자가 저장하면 열려 있는 홈도 바뀐다)
  useEffect(() => {
    let alive = true;
    const pull = () => void loadSiteText().then((t) => alive && setText(t));
    pull();
    const off = subscribeContentUpdates((u) => {
      if (u.type === "sitetext") {
        resetSiteText(); // 캐시를 비우고 다시 읽는다 — 안 그러면 고친 문구가 안 보인다
        pull();
      }
    });
    return () => {
      alive = false;
      off();
    };
  }, []);

  // (내비 정리) 스테이지 우측 메뉴는 하단 탭바와 100% 중복이라 제거 — 이동은 하단 네비/CTA로 일원화.
  // (2026-07 커뮤니티 실구현으로 '준비 중' 안내 헬퍼 제거 — 하단 7메뉴 전부 실동작)

  // 상단바 유틸 — 실제 동작으로 연결(자동재생 금지·무수집 준수).
  function toggleMusic() {
    const on = !getSoundPref();
    setSoundPref(on); // 저장(체험존 입장 시 반영). Howler 비의존.
    flash(on ? "소리를 켰어요 🔊 (체험존에서 들려요)" : "소리를 껐어요 🔇");
    track({ name: "set_pref", params: { pref: "sound", on } });
  }
  function openNews() {
    // 소식 — 진행 중 행사는 아래 EventBanner(콘텐츠 주도)로 노출. 별도 소식 없으면 정직하게 안내.
    flash(text.newsEmpty);
  }

  function openCustomizer() {
    setCustomizing(true);
  }

  /**
   * 🚪 **첫 화면의 갈림길은 여기 한 곳에서만 센다.**
   *
   * 🔴 예전엔 문 하나하나에 `track` 을 손으로 붙였다. 그래서 열넷 중 **셋만** 세어졌다 —
   * 큰 [체험 공간 입장]도, 하단 [도서관]·[체험존]·[이벤트]·[마이룸]도 전부 어두웠다.
   * 운영 기록은 넉 달 내내 `create_hero` **하루 1건**뿐이었고, 그건 내 점검이었다.
   *
   * 「첫 화면에서 아이가 어디로 가나」가 이 숫자의 유일한 쓸모다(설명에도 그렇게 적혀 있다).
   * 셋만 보고는 「아무도 아무것도 안 누른다」로 읽힌다 — 그 오해가 곧 화면을 뜯어고치게 만든다.
   *
   * 붙이는 자리를 **하나로 두면** 새 문이 생겨도 저절로 따라온다.
   */
  function goEntry(entry: string, go: () => void) {
    track({ name: "select_entry", params: { entry } });
    go();
  }
  /**
   * 🚪 **「체험 공간 입장」은 체험 공간으로 간다.**
   *
   * 예전엔 제 친구가 없는 아이를 **꾸미기 창으로** 돌렸다. 그래서 큰 단추와 하단 [체험존]이
   * 둘 다 「들어간다」고 써 놓고 **캐릭터 편집기**를 열었다 — 버튼이 약속한 것을 안 한 것이다.
   *
   * 그 사이 정작 그 상황을 위해 만든 화면(`/v`)에는 **아무도 못 갔다.**
   * 거기에는 고를 말이 이미 있다: 「아직 히어로가 없어요. 기본 히어로로 들어가거나,
   * 먼저 나만의 히어로를 만들 수 있어요」(`verseNoteNew`) — 그게 죽은 글이 되어 있었다.
   *
   * 아이 쪽에서 보면 더 나쁘다: 들어가려고 눌렀는데 편집기가 뜨고, 닫으면 **제자리**다.
   * 집계가 그 모양이었다 — 여권 코드 91, 체험공간 입장 7, 제 친구 만들기 1(2026-08-31).
   *
   * 이제 갈래를 이름대로 나눈다:
   *  · [체험 공간 입장]·[체험존] → **체험 공간**(`/v`)에서 고른다
   *  · [🧒 나만의 친구 만들기] → **꾸미기**(첫 화면에 따로 있다)
   */
  function enterWorld() {
    navigate(ROUTES.verse);
  }

  // UI 그림은 등록부(shared UI_ART)에서 해석 — 게시본 → 번들 → 이모지.
  // 경로를 여기 박아 두면 운영자가 버튼 그림을 못 바꾼다.
  const uiArt = useUiArt();
  /**
   * 📚 수업 중에는 굿즈·커뮤니티·이벤트를 숨긴다. 아이 스물다섯이 각자 다른 곳으로
   * 흩어지면 선생님이 수업을 못 하고, 학교는 수업 중에 상품이 보이는 것에 특히 민감하다.
   */
  const inClass = useInClass();
  /**
   * 🎚️ 운영자가 **어디에 쓰느냐에 맞춰 켜고 끈 것**. 못 받아 오면 다 보인다(불변식 3).
   * 수업 모드 기본값도 이 안에 함께 산다 — 목록이 두 곳에 있으면 조용히 갈린다.
   */
  const feat = useUiFeatures();
  /** 🧸 영유아 모드 — 메뉴를 셋으로 줄이고 보호자 칸을 하나 놓는다(조치계획서 ■1). */
  const kids = useKids();

  // 하단 고정 네비(7) — 존재 라우트 우선, 미구현은 안내. img=손그림 아이콘(없으면 emoji 폴백)
  const NAV = [
    { key: "nav.myroom", label: "마이룸", icon: "🧒", img: uiArt("nav", "myroom"), action: openCustomizer },
    { key: "nav.library", label: "도서관", icon: "📚", img: uiArt("nav", "library"), action: () => navigate(ROUTES.book) },
    { key: "nav.verse", label: "체험존", icon: "⭐", img: uiArt("nav", "verse"), action: enterWorld },
    { key: "nav.event", label: "이벤트", icon: "🎁", img: uiArt("nav", "event"), action: () => navigate(`${ROUTES.world}?zone=event`) },
    {
      key: "nav.store",
      label: "굿즈샵",
      icon: "🛍️",
      img: uiArt("nav", "store"),
      action: () => {
        // 갈림길 집계는 가운데(goEntry)에서 한다 — 여기선 굿즈 쪽 신호만
        track({ name: "click_store", params: { source: "landing" } });
        navigate(ROUTES.goods);
      },
    },
    {
      key: "nav.community",
      label: "커뮤니티",
      icon: "💬",
      img: uiArt("nav", "community"),
      action: () => navigate(ROUTES.community),
    },
    { key: "nav.settings", label: "설정", icon: "⚙️", img: uiArt("nav", "settings"), action: () => setSettingsOpen(true) },
  ].filter((n) => uiVisible(n.key, feat, inClass, kids));

  /**
   * 🧸 **영유아 모드면 보호자 칸 하나를 더 놓는다** — 숨긴 것을 **없앤 것이 아니라 옮긴** 자리다.
   *
   * 🔴 이 칸이 없으면 보호자가 굿즈·행사·소식으로 갈 길이 첫 화면에서 사라진다.
   * 「숨기고 끝」이면 운영자는 「굿즈가 없어졌다」로 겪는다(조치계획서 ■1·■9).
   */
  const navAll = kids
    ? [
        // 🔴 약속한 차례대로 **직접 세운다** — 「전자책 · 마법 · 체험공간」(조치계획서 ■1).
        //    NAV 를 걸러 쓰면 마법이 빠진다(마법은 평소 하단 메뉴에 없고 주소로만 들어간다).
        ...NAV.filter((n) => n.key === "nav.library"),
        ...(uiVisible("route.ar", feat, inClass, kids)
          ? [
              {
                key: "nav.magic",
                label: "마법",
                icon: "✨",
                img: uiArt("nav", "magic"),
                action: () => navigate(ROUTES.ar),
              },
            ]
          : []),
        ...NAV.filter((n) => n.key === "nav.verse"),
        {
          key: "nav.family",
          label: "보호자",
          icon: "🔒",
          img: uiArt("nav", "family"),
          action: () => navigate(ROUTES.family),
        },
      ]
    : NAV;

  return (
    <main className="home" data-decor={decor ? "on" : undefined} data-kids={kids || undefined}>
      {/* 상단 바 */}
      <header className="home__top">
        <span className="home__logo">🐣 경주 그림책방</span>
        <div className="home__top-actions">
          <button type="button" aria-label="소리 켜기/끄기" onClick={toggleMusic}>
            <UiTopIcon name="music" ready={decor} />
          </button>
          <button type="button" aria-label="소식" onClick={openNews}>
            <UiTopIcon name="bell" ready={decor} />
          </button>
          <button type="button" aria-label="설정" onClick={() => setSettingsOpen(true)}>
            <UiTopIcon name="menu" ready={decor} />
          </button>
        </div>
      </header>

      {/* 히어로 무대(배경 + 이펙트 + 캐릭터 + 메뉴 + CTA) */}
      <section className="home__stage" data-effect={EFFECT}>
        {assets.bg ? (
          <img
            ref={bgRef}
            className="home__bg"
            src={assets.bg.url}
            onLoad={() => setDecor(true)}
            onError={() => setDecor(true)}
            alt=""
            /* 첫 화면에서 가장 큰 그림 = LCP 요소. index.html 의 preload 와 짝이다 —
               브라우저가 이 그림을 다른 리소스보다 먼저 처리하게 한다(FR-LAND-01). */
            /**
             * ⚠️ 소문자 `fetchpriority` 다 — **카멜로 쓰면 React 18 이 모른다.**
             * 경고를 내면서 **속성을 통째로 버려서**, 우선 로딩 힌트가 안 걸린다
             * (React 19 부터 카멜을 안다). 소문자 속성은 그대로 DOM 에 나간다.
             */
            {...{ fetchpriority: "high" }}
            decoding="async"
            style={assets.bg.pos ? { objectPosition: `${assets.bg.pos.x * 100}% ${assets.bg.pos.y * 100}%` } : undefined}
          />
        ) : null}
        <div className="home__fx" aria-hidden="true">
          {Array.from({ length: 14 }, (_, i) => (
            <span key={i} style={{ "--i": i } as CSSProperties} />
          ))}
        </div>

        <div className="home__banner">
          {/**
            * 🔴 **운영 상태 알림은 보호자 것이다**(「체험판 공개」 같은 말).
            * 영유아 모드에서는 뺀다 — 아이는 못 읽고, 읽을 수 있는 아이에게도 알 바 아니다.
            */}
          {text.milestone && !kids ? <span className="home__milestone">{text.milestone}</span> : null}
          <h1 className="home__title">
            {text.title1}
            <br />
            <strong>{text.title2}</strong>
          </h1>
        </div>

        <div className="home__chars">
          {/* 게시된 모든 메인 캐릭터를 렌더(운영자가 관리자에서 추가하면 자동 반영). 스트립 있으면 애니 재생. */}
          {/* 캐릭터 띠는 큰 그림 다음 — 위 `decor` 주석 */}
          {(decor ? assets.chars : []).map((c) => {
            const alt = CHAR_ALT[c.id] ?? "메인 캐릭터";
            return (
              <div
                key={c.id}
                className="home__char-wrap"
                style={pieceStyle(
                  stagePosForWidth(c.id, c.pos ?? landingCharHome(c.id), narrow, c.posNarrow),
                  landingCharHome(c.id),
                  c.scale,
                  c.z,
                )}
              >
                {c.strip ? (
                  <SpriteStrip
                    url={c.strip.url}
                    alt={alt}
                    className={`home__char home__char--${c.id}`}
                    dataAnim={CHAR_ANIM}
                    fps={c.strip.fps ?? 1.8}
                  />
                ) : (
                  <img className={`home__char home__char--${c.id}`} data-anim={CHAR_ANIM} src={c.url} alt={alt} />
                )}
              </div>
            );
          })}
          {/* 내 히어로 — 마스코트와 나란히 무대에 등장. 탭하면 꾸미기 열림. */}
          <button
            type="button"
            className="home__char-wrap home__hero"
            style={pieceStyle(undefined, HERO_DEF)}
            onClick={() => setCustomizing(true)}
            aria-label="나의 히어로 — 탭해서 꾸미기"
            title="탭해서 꾸미기"
          >
            {/* 대기 동작: 숨쉬기+가끔 폴짝(전용 keyframes) — HeroAvatar 자체 idle 비활성(이중 애니 방지) */}
            <span className="home__hero-sprite">
              {decor ? <HeroAvatar hero={draft} size={216} idle={false} /> : null}
            </span>
            <span className="home__hero-name">{heroDisplayName(draft)}</span>
          </button>
          <span
            className="home__bubble"
            style={{
              // 말풍선은 왼쪽 캐릭터 머리 위 — 자리 기본값은 shared 한 곳을 본다
              left: `${(gregChar?.pos?.x ?? landingCharHome("greg").x) * 100}%`,
              top: `${((gregChar?.pos?.y ?? landingCharHome("greg").y) - 0.2) * 100}%`,
            }}
          >
            {text.bubble}
          </span>
        </div>

        <button type="button" className="home__cta" onClick={() => goEntry("enter_world", enterWorld)}>
          {/* 문구는 운영자 것(SiteText) — 그림을 옆에 두니 앞뒤 이모지만 뗀다 */}
          <UiIcon name="door" size={30} /> {withoutEdgeEmoji(text.cta)}
        </button>

      </section>

      {/* 읽다 만 책이 있을 때만. 문구는 운영자가 고친다(SiteText.resumeCta).
          🔴 무대(section) **밖**에 둔다 — 무대는 배경 그림(.home__bg, inset:0)이 덮고 그 위에
          큰 버튼만 절대배치로 얹는 구조라, 그 안에 흐름 배치로 넣으면 **보이는데 눌리지 않는다**
          (실제로 그랬다 — elementFromPoint 가 .home__bg 를 돌려줬다). */}
      {resume ? (
        <button
          type="button"
          className="home__resume"
          onClick={() =>
            // 🔴 쪽수를 같이 넘긴다 — 안 넘기면 「7쪽」이라 적어 놓고 1쪽을 연다
            navigate(`${ROUTES.book}?id=${encodeURIComponent(resume.id)}&page=${resume.page}`)
          }
        >
          📖 {text.resumeCta} — <b>{resume.title}</b> <span>{resume.page}쪽</span>
        </button>
      ) : null}

      {/* 행사 배너(FR-LAND-03) — 진행/예정 행사 있을 때만 노출(콘텐츠 주도) */}
      <EventBanner />

      {/* 재방문 환영(히어로) */}
      {saved ? (
        <div className="home__welcome">
          {decor ? <HeroAvatar hero={draft} size={56} idle /> : null}
          <p>
            <strong>{draft.nickname ?? "꼬마 탐험가"}</strong>님, 다시 왔군요!{rallyDone ? " 🏅" : ""}
          </p>
          <button type="button" className="btn" onClick={() => goEntry("edit_hero", openCustomizer)}>
            히어로 수정
          </button>
        </div>
      ) : (
        <>
          {/* 🧒 **처음 온 아이에게 「나만의 친구 만들기」.**

              예전엔 이 자리에 안내 문구만 있었고, 제 친구를 만드는 길은 **다음 화면**에 있었다
              (거기서도 큰 단추는 기본 히어로로 [들어가기]였다). 그래서 여권 코드 91개에
              **제 친구를 끝까지 만든 아이는 1명**이었다.

              🔴 큰 입장 단추를 **밀어내지 않는다.** 바로 놀고 싶은 아이가 대부분이고 그 길을
                 좁히면 안 된다 — 이건 그 아래 한 줄이다.
              🔴 문구를 비우면 이 단추만 사라진다(운영자 것 — SiteText). */}
          {text.makeHeroCta ? (
            <button type="button" className="home__makehero" onClick={() => goEntry("create_hero", openCustomizer)}>
              <UiIcon name="maker" size={26} /> {withoutEdgeEmoji(text.makeHeroCta)}
            </button>
          ) : null}
          {/**
            * 🔴 **「가입·로그인 없이 …」는 보호자에게 하는 말이다.**
            *
            * 영유아 모드에서는 뺀다. 지우는 것이 아니라 **옮긴 것**이다 — 같은 고지가
            * 보호자 화면의 「📖 함께 보는 법」과 설정에 그대로 있고, 영유아 모드를 켜는
            * 사람이 바로 그 보호자다(`landingKids.test.tsx` 가 그 길을 지킨다).
            */}
          {kids ? null : <p className="home__note">{text.note}</p>}
        </>
      )}

      {/* 👶 몇 살 것인지 — 보호자가 처음 보는 곳이라 영유아 모드에서도 둔다(■9). 2026-10-03 폰에서 걸어 보고 자리를 두 번 옮겼다:
          🔴 무대 **안**(제목 아래)은 안 된다 — 폰에서 세 줄로 접혀 캐릭터·말풍선을 덮었다(무대 안 캐릭터는 절대배치라 안 밀린다).
          🔴 무대 **바로 아래**도 안 된다 — 「나만의 친구 만들기」를 아래 고정 메뉴 뒤로 밀어냈다. 아이의 길이 먼저, 보호자 글은 그 뒤. */}
      <AgeBands text={text.ageBands} className="home__ages" />

      {/* 하단 고정 네비(7) · 영유아 모드에서는 넷(전자책·마법·체험공간·보호자)으로 줄고 커진다 */}
      <nav className="home__nav" aria-label="하단 메뉴" data-kids={kids || undefined}>
        {navAll.map((n) => (
          <button
            key={n.key}
            type="button"
            onClick={() => goEntry(n.key.replace(/^nav\./, ""), n.action)}
            /* 🔴 키는 `nav.verse` 다 — `"verse"` 와 비교해서 **한 번도 참이 안 됐다**.
               그래서 대표 진입의 금색 강조(landing.css)가 넉 달 동안 안 켜졌다. */
            data-primary={n.key === "nav.verse" ? "true" : undefined}
          >
            <span className="home__nav-icon" aria-hidden="true">
              {/* 큰 그림 전에는 **같은 크기의 빈 칸** — 이모지를 띄웠다 바꾸면 한 번 더 깜빡인다 */}
              {n.img ? (
                decor ? (
                  <img className="home__nav-img" src={n.img} alt="" />
                ) : (
                  <span className="home__nav-img" />
                )
              ) : (
                n.icon
              )}
            </span>
            <span className="home__nav-label">{n.label}</span>
          </button>
        ))}
      </nav>

      {customizing ? (
        <HeroCustomizer
          onSaved={() => {
            setCustomizing(false);
            navigate(ROUTES.verse);
          }}
          onClose={() => setCustomizing(false)}
        />
      ) : null}

      {settingsOpen ? <SettingsSheet onClose={() => setSettingsOpen(false)} /> : null}

      {toast ? (
        <div className="home__toast" role="status" aria-live="polite">
          {toast}
        </div>
      ) : null}
    </main>
  );
}

/** 상단 작은 버튼 그림 — 게시본 → 번들 → 이모지. `ready` 전에는 빈 칸(큰 그림이 먼저). */
function UiTopIcon({ name, ready }: { name: string; ready: boolean }) {
  const art = useUiArt();
  const url = art("top", name);
  if (!url) return <span aria-hidden="true">{uiArtSlot("top", name)?.emoji ?? "•"}</span>;
  if (!ready) return <span className="home__top-wait" aria-hidden="true" />;
  return <img src={url} alt="" />;
}

/**
 * 큰 그림을 이만큼만 기다린다. 느린 4G(1.6Mbps)에서 큰 그림은 약 1.8초에 온다 —
 * 그보다 넉넉히 두되, 실패했을 때 메뉴 그림이 너무 오래 비지 않게.
 */
const DECOR_WAIT_MS = 3000;
